import { useState, useEffect, useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Plus, MousePointerClick, ArrowDown, Clock, Trash2, Loader2, Zap } from "lucide-react";

interface PopupRow {
  readonly id: string;
  readonly name: string;
  readonly trigger_type: string;
  readonly trigger_value: string | null;
  readonly is_active: number;
  readonly starts_at: string | null;
  readonly expires_at: string | null;
  readonly impressions: number;
  readonly conversions: number;
  readonly created_at: string;
}

const TRIGGER_CONFIG: Record<string, { icon: ReactNode; label: string; className: string }> = {
  exit_intent: {
    icon: <MousePointerClick className="h-4 w-4" />,
    label: "יציאה מהדף",
    className: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
  },
  scroll: {
    icon: <ArrowDown className="h-4 w-4" />,
    label: "גלילה",
    className: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30",
  },
  timed: {
    icon: <Clock className="h-4 w-4" />,
    label: "זמן",
    className: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  },
};

function getStatus(popup: PopupRow): { label: string; className: string } {
  if (!popup.is_active) return { label: "לא פעיל", className: "bg-slate-500/15 text-slate-500 border-slate-500/30" };
  const now = new Date().toISOString();
  if (popup.starts_at && popup.starts_at > now) return { label: "מתוזמן", className: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" };
  if (popup.expires_at && popup.expires_at < now) return { label: "פג תוקף", className: "bg-destructive/15 text-destructive border-destructive/30" };
  return { label: "פעיל", className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" };
}

function conversionRate(impressions: number, conversions: number): string {
  if (impressions === 0) return "0%";
  return `${((conversions / impressions) * 100).toFixed(1)}%`;
}

function StatChip({ label, value }: { readonly label: string; readonly value: string | number }): ReactNode {
  return (
    <div className="flex flex-col items-center bg-muted/50 rounded-lg px-3 py-2 min-w-[60px]">
      <span className="text-xs font-semibold">{value}</span>
      <span className="text-xs text-muted-foreground mt-0.5">{label}</span>
    </div>
  );
}

export function PopupListPage(): ReactNode {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [popups, setPopups] = useState<readonly PopupRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPopups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/popups/admin", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: readonly PopupRow[] };
      if (json.success) setPopups(json.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { void fetchPopups(); }, [fetchPopups]);

  const toggleActive = useCallback(
    async (id: string, currentActive: number) => {
      await fetch(`/api/popups/admin/${id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: currentActive === 0 }),
      });
      void fetchPopups();
    },
    [accessToken, fetchPopups],
  );

  const deletePopup = useCallback(
    async (id: string) => {
      await fetch(`/api/popups/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      void fetchPopups();
    },
    [accessToken, fetchPopups],
  );

  return (
    <div className="p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">חלונות קופצים</h1>
          {popups.length > 0 && (
            <p className="text-sm text-muted-foreground mt-0.5">{popups.length} פופאפים</p>
          )}
        </div>
        <Button onClick={() => navigate("/popups/new")} className="min-h-[38px]">
          <Plus className="h-4 w-4 me-1.5" />
          יצירת פופאפ
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : popups.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <Zap className="h-8 w-8 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-medium">אין חלונות קופצים עדיין</p>
              <p className="text-sm text-muted-foreground mt-1">צור פופאפ כדי להגדיל המרות</p>
            </div>
            <Button onClick={() => navigate("/popups/new")}>
              <Plus className="h-4 w-4 me-1" />
              יצירת פופאפ ראשון
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {popups.map((popup) => {
            const status = getStatus(popup);
            const trigger = TRIGGER_CONFIG[popup.trigger_type];
            const rate = conversionRate(popup.impressions, popup.conversions);

            return (
              <Card
                key={popup.id}
                className="cursor-pointer hover:border-primary/40 transition-colors shadow-sm"
                onClick={() => navigate(`/popups/${popup.id}`)}
              >
                <CardContent className="p-4 space-y-4">
                  {/* Top row: trigger badge + name + status */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      {trigger && (
                        <span className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold shrink-0",
                          trigger.className
                        )}>
                          {trigger.icon}
                          {trigger.label}
                        </span>
                      )}
                    </div>
                    <span className={cn(
                      "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold shrink-0",
                      status.className
                    )}>
                      {status.label}
                    </span>
                  </div>

                  {/* Name */}
                  <p className="font-semibold text-base leading-tight">{popup.name}</p>

                  {/* Trigger detail */}
                  {popup.trigger_value && (
                    <p className="text-xs text-muted-foreground">
                      {popup.trigger_type === "scroll"
                        ? `גלילה ${popup.trigger_value}%`
                        : popup.trigger_type === "timed"
                        ? `אחרי ${Number(popup.trigger_value) / 1000} שניות`
                        : popup.trigger_value}
                    </p>
                  )}

                  {/* Stats row */}
                  <div className="flex gap-2">
                    <StatChip label="חשיפות" value={popup.impressions.toLocaleString()} />
                    <StatChip label="המרות" value={popup.conversions.toLocaleString()} />
                    <StatChip label="שיעור" value={rate} />
                  </div>

                  {/* Action row */}
                  <div
                    className="flex items-center justify-between pt-1 border-t"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant={popup.is_active === 1 ? "outline" : "default"}
                      size="sm"
                      className="min-h-[40px]"
                      onClick={() => void toggleActive(popup.id, popup.is_active)}
                    >
                      {popup.is_active === 1 ? "כבוי" : "הפעל"}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="min-h-[40px] min-w-[40px] p-0 text-destructive hover:text-destructive"
                      onClick={() => void deletePopup(popup.id)}
                      title="מחק פופאפ"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
